import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { customFetch } from '../apiClient';

const initialForm = {
  loginID: '',
  User_Name: '',
  password: '',
  MobileNo: '',
  selectedModules: [],
};

const UserCreation = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [modules, setModules] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingModules, setLoadingModules] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const formRef = useRef(null);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        setLoadingModules(true);
      const res = await customFetch('/modules');
        if (!res.ok) throw new Error('Failed to load modules');
        const data = await res.json();
        setModules(data);
      } catch (err) {
        setError(err.message || 'Error loading modules');
      } finally {
        setLoadingModules(false);
      }
    };
    fetchModules();
  }, []);

  useEffect(() => {
    if (formRef.current) {
      const firstInput = formRef.current.querySelector(
        'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
      );
      firstInput?.focus();
    }
  }, []);

  const handleKeyDown = useCallback((e) => {
    const focusableElements = formRef.current?.querySelectorAll(
      'input:not([disabled]):not([type="hidden"]):not([type="checkbox"]), select:not([disabled]), textarea, button:not([disabled])'
    );
    if (!focusableElements || focusableElements.length === 0) return;

    const currentIndex = Array.from(focusableElements).indexOf(e.target);

    if (e.key === 'Enter' && currentIndex === focusableElements.length - 1) {
      e.target.closest('form').requestSubmit();
      return;
    }

    if (['Enter', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
      focusableElements[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = Math.max(0, currentIndex - 1);
      focusableElements[prevIndex]?.focus();
    }
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (formRef.current && e.target.closest('form') === formRef.current) {
        handleKeyDown(e);
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown]);

  const validateField = (name, value) => {
    switch (name) {
      case 'loginID':
        if (!value.trim()) return 'Required';
        if (value.length < 3) return 'Min 3 chars';
        return '';
      case 'User_Name':
        if (!value.trim()) return 'Required';
        if (value.length < 2) return 'Min 2 chars';
        return '';
      case 'password':
        if (!value) return 'Required';
        if (value.length < 6) return 'Min 6 chars';
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) 
          return 'Requires uppercase, lowercase & number';
        return '';
      case 'MobileNo':
        if (value && !/^\d{10}$/.test(value)) return 'Must be 10 digits';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    
    if (touched[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setFieldErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleModuleChange = (e) => {
    const { value, checked } = e.target;
    setForm((prev) => {
      const newSelected = new Set(prev.selectedModules);
      if (checked) {
        newSelected.add(value);
      } else {
        newSelected.delete(value);
      }
      return { ...prev, selectedModules: Array.from(newSelected) };
    });
  };

  const selectAllModules = () => {
    const allModules = modules.map(m => m.modules);
    setForm(prev => ({ ...prev, selectedModules: allModules }));
  };

  const clearAllModules = () => {
    setForm(prev => ({ ...prev, selectedModules: [] }));
  };

  const validate = () => {
    const errors = {};
    errors.loginID = validateField('loginID', form.loginID);
    errors.User_Name = validateField('User_Name', form.User_Name);
    errors.password = validateField('password', form.password);
    errors.MobileNo = validateField('MobileNo', form.MobileNo);

    const hasErrors = Object.values(errors).some(err => err !== '');
    if (hasErrors) {
      setFieldErrors(errors);
      setTouched({ loginID: true, User_Name: true, password: true, MobileNo: true });
      return 'Please fix form errors';
    }

    if (form.selectedModules.length === 0) return 'Select at least one module';
    return '';
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength: 33, label: 'Weak', color: '#e74c3c' };
    if (strength <= 3) return { strength: 66, label: 'Fair', color: '#f39c12' };
    return { strength: 100, label: 'Strong', color: '#27ae60' };
  };

  const passwordStrength = getPasswordStrength(form.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
    
      const hospitalId = sessionStorage.getItem('hospitalId');
      const modulesString = form.selectedModules.join(',');

      const payload = {
        loginID: form.loginID,
        User_Name: form.User_Name,
        password: form.password,
        MobileNo: form.MobileNo,
        ModuleList: modulesString,
        hid: hospitalId,
      };

     const res = await customFetch(`/hospitals/${hospitalId}/users`, {
  method: 'POST',
  body: JSON.stringify(payload),
});

    if (!res.ok) {
          let errorMsg = 'Failed to create user';
          try {
            // Try to parse as JSON first (to catch our new limit message)
            const errorData = await res.json();
            errorMsg = errorData.message || errorData.title || errorMsg;
          } catch (parseErr) {
            // Fallback to text if it's not JSON
            const text = await res.text();
            errorMsg = text || errorMsg;
          }
          throw new Error(errorMsg);
        }

      setSuccess('User created successfully!');
      setForm(initialForm);
      setTouched({});
      setFieldErrors({});
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = () => {
    navigate('/master/users-list');
  };

  const handleReset = () => {
    setForm(initialForm);
    setTouched({});
    setFieldErrors({});
    setError('');
    setSuccess('');
  };

  const getFieldClass = (fieldName) => {
    if (!touched[fieldName]) return 'form-control-custom';
    if (fieldErrors[fieldName]) return 'form-control-custom error';
    if (form[fieldName]) return 'form-control-custom success';
    return 'form-control-custom';
  };

  return (
    <div className="user-creation-container">
      <div className="creation-card">
        {/* Header */}
        <div className="creation-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-person-plus-fill"></i>
            </div>
            <div className="header-text">
              <h5>Create New User</h5>
            </div>
          </div>
          <button className="btn-view-list" onClick={handleButtonClick} title="View Users List">
            <i className="bi bi-list-ul"></i>
            Users List
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError('')}><i className="bi bi-x"></i></button>
          </div>
        )}
        {success && (
          <div className="alert-custom alert-success">
            <i className="bi bi-check-circle-fill"></i>
            <span>{success}</span>
            <button className="alert-close" onClick={() => setSuccess('')}><i className="bi bi-x"></i></button>
          </div>
        )}

        {/* Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="creation-form">
          
          {/* User Details Section */}
          <div className="form-section">
            <div className="section-title">
              <i className="bi bi-person-circle"></i>
              <span>User Details</span>
            </div>
            <div className="section-content">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="loginID">Login ID <span className="required">*</span></label>
                  <div className="input-wrapper">
                    <input
                      id="loginID" name="loginID" type="text"
                      className={getFieldClass('loginID')}
                      value={form.loginID} onChange={handleChange} onBlur={handleBlur}
                      placeholder="Unique ID" tabIndex={1} required
                    />
                  </div>
                  {touched.loginID && fieldErrors.loginID && <small className="error-text">{fieldErrors.loginID}</small>}
                </div>

                <div className="form-group">
                  <label htmlFor="User_Name">Username <span className="required">*</span></label>
                  <div className="input-wrapper">
                    <input
                      id="User_Name" name="User_Name" type="text"
                      className={getFieldClass('User_Name')}
                      value={form.User_Name} onChange={handleChange} onBlur={handleBlur}
                      placeholder="Full name" tabIndex={2} required
                    />
                  </div>
                  {touched.User_Name && fieldErrors.User_Name && <small className="error-text">{fieldErrors.User_Name}</small>}
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password <span className="required">*</span></label>
                  <div className="input-wrapper">
                    <input
                      id="password" name="password" type={showPassword ? "text" : "password"}
                      className={getFieldClass('password')}
                      value={form.password} onChange={handleChange} onBlur={handleBlur}
                      placeholder="Min 6 chars" tabIndex={3} required
                    />
                    <button
                      type="button" className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)} tabIndex={-1}
                    >
                      <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                  </div>
                  {form.password && (
                    <div className="password-strength">
                      <div className="strength-bar">
                        <div className="strength-fill" style={{ width: `${passwordStrength.strength}%`, background: passwordStrength.color }}></div>
                      </div>
                      <span className="strength-label" style={{ color: passwordStrength.color }}>{passwordStrength.label}</span>
                    </div>
                  )}
                  {touched.password && fieldErrors.password && <small className="error-text">{fieldErrors.password}</small>}
                </div>

                <div className="form-group">
                  <label htmlFor="MobileNo">Mobile Number</label>
                  <div className="input-wrapper">
                    <input
                      id="MobileNo" name="MobileNo" type="text"
                      className={getFieldClass('MobileNo')}
                      value={form.MobileNo} onChange={handleChange} onBlur={handleBlur}
                      placeholder="10 digits" maxLength="10" tabIndex={4}
                    />
                  </div>
                  {touched.MobileNo && fieldErrors.MobileNo && <small className="error-text">{fieldErrors.MobileNo}</small>}
                </div>
              </div>
            </div>
          </div>

          {/* Modules Section */}
          <div className="form-section">
            <div className="section-title">
              <i className="bi bi-grid-3x3-gap"></i>
              <span>Module Permissions</span>
              <div className="module-actions">
                <button type="button" className="btn-module-action" onClick={selectAllModules} disabled={loadingModules}>
                  Select All
                </button>
                <button type="button" className="btn-module-action" onClick={clearAllModules} disabled={loadingModules}>
                  Clear All
                </button>
              </div>
            </div>
            
            <div className="section-content">
              {loadingModules ? (
                <div className="loading-modules">
                  <div className="spinner-custom"></div>
                  <p>Loading...</p>
                </div>
              ) : modules.length === 0 ? (
                <div className="empty-modules">
                  <p>No modules available</p>
                </div>
              ) : (
                <>
                  <div className="modules-grid">
                    {modules.map((m) => (
                      <label key={m.MID} className="module-checkbox">
                        <input
                          type="checkbox" value={m.modules}
                          checked={form.selectedModules.includes(m.modules)}
                          onChange={handleModuleChange}
                        />
                        <span className="checkbox-custom"></span>
                        <span className="module-name">{m.modules}</span>
                      </label>
                    ))}
                  </div>
                  <div className="selected-count">
                    <span>{form.selectedModules.length} / {modules.length} selected</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={handleReset}>
              Reset
            </button>
            <button type="submit" className="btn-primary" disabled={loading} tabIndex={6}>
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .user-creation-container {
          padding: 0;
          font-family: system-ui, -apple-system, sans-serif;
        }

        .creation-card {
          background: white;
          border-radius: 6px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }

        /* Header */
        .creation-header {
          background: var(--theme-color, #0088cc);
          padding: 8px 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid #00d4aa;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .header-icon {
          width: 24px;
          height: 24px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 14px;
        }

        .header-text h5 {
          margin: 0;
          color: white;
          font-size: 14px;
          font-weight: 600;
        }

        .btn-view-list {
          padding: 4px 10px;
          border-radius: 4px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.15);
          color: white;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
          transition: 0.2s;
        }

        .btn-view-list:hover {
          background: white;
          color: var(--theme-color, #0088cc);
        }

        /* Alerts */
        .alert-custom {
          margin: 8px 12px 0;
          padding: 6px 10px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 500;
          position: relative;
        }
        .alert-danger { background: #fff5f5; color: #e74c3c; border: 1px solid #feb2b2; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }
        .alert-close {
          position: absolute; right: 8px; background: none; border: none; cursor: pointer; opacity: 0.6;
        }

        /* Form Layout */
        .creation-form {
          padding: 12px;
        }

        .form-section {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          margin-bottom: 12px;
        }

        .section-title {
          background: var(--theme-color, #f7fafc);
          color: white; /* If theme color is dark, else switch to #2d3748 if light */
          padding: 6px 10px;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
          border-bottom: 1px solid #e2e8f0;
        }

        .module-actions {
          margin-left: auto;
          display: flex;
          gap: 6px;
        }

        .btn-module-action {
          padding: 2px 8px;
          border-radius: 3px;
          border: 1px solid rgba(255, 255, 255, 0.4);
          background: transparent;
          color: white;
          font-size: 10px;
          cursor: pointer;
        }
        .btn-module-action:hover:not(:disabled) { background: rgba(255,255,255,0.2); }

        .section-content {
          padding: 10px;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        /* Form Inputs */
        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group label {
          font-size: 11px;
          font-weight: 600;
          color: #4a5568;
          margin-bottom: 4px;
        }

        .required { color: #e53e3e; }

        .input-wrapper { position: relative; }

        .form-control-custom {
          width: 100%;
          padding: 6px 8px;
          border: 1px solid #cbd5e0;
          border-radius: 4px;
          font-size: 12px;
          color: #2d3748;
          outline: none;
          transition: border-color 0.2s;
        }

        .form-control-custom:focus {
          border-color: var(--theme-color, #3182ce);
          box-shadow: 0 0 0 1px var(--theme-color, #3182ce);
        }

        .form-control-custom.error { border-color: #e53e3e; background: #fff5f5; }
        
        .password-toggle {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #a0aec0;
          cursor: pointer;
          font-size: 13px;
        }

        .error-text {
          color: #e53e3e;
          font-size: 10px;
          margin-top: 3px;
        }

        .password-strength {
          margin-top: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .strength-bar {
          flex: 1;
          height: 4px;
          background: #edf2f7;
          border-radius: 2px;
        }

        .strength-fill { height: 100%; border-radius: 2px; transition: 0.3s; }
        .strength-label { font-size: 10px; font-weight: 600; }

        /* Modules */
        .loading-modules, .empty-modules {
          text-align: center;
          padding: 20px;
          font-size: 12px;
          color: #718096;
        }

        .spinner-custom {
          width: 24px; height: 24px;
          border: 3px solid #edf2f7;
          border-top-color: var(--theme-color, #3182ce);
          border-radius: 50%;
          margin: 0 auto 10px;
          animation: spin 1s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .modules-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 6px;
        }

        .module-checkbox {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 8px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          background: #fff;
          transition: 0.2s;
        }

        .module-checkbox:hover { border-color: #cbd5e0; background: #f7fafc; }
        .module-checkbox input[type="checkbox"] { display: none; }

        .checkbox-custom {
          width: 14px;
          height: 14px;
          border: 1px solid #cbd5e0;
          border-radius: 3px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .module-checkbox input:checked ~ .checkbox-custom {
          background: var(--theme-color, #3182ce);
          border-color: var(--theme-color, #3182ce);
        }

        .module-checkbox input:checked ~ .checkbox-custom::after {
          content: '✓'; color: white; font-size: 10px; font-weight: bold;
        }

        .module-name { font-size: 11px; color: #2d3748; }

        .selected-count {
          margin-top: 8px;
          font-size: 11px;
          color: #4a5568;
          font-weight: 600;
          padding: 4px 8px;
          background: #edf2f7;
          border-radius: 4px;
          display: inline-block;
        }

        /* Form Actions */
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }

        .btn-primary, .btn-secondary {
          padding: 6px 14px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          border: none;
          cursor: pointer;
        }

        .btn-primary {
          background: var(--theme-color, #3182ce);
          color: white;
        }
        .btn-primary:hover:not(:disabled) { opacity: 0.9; }
        .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

        .btn-secondary { background: #edf2f7; color: #4a5568; border: 1px solid #cbd5e0; }
        .btn-secondary:hover { background: #e2e8f0; }

        /* Responsive */
        @media (max-width: 768px) {
          .form-row { grid-template-columns: 1fr 1fr; }
          .modules-grid { grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); }
        }
        @media (max-width: 480px) {
          .form-row { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default UserCreation;